/*
  reveal.js

  Fades sections up as they enter the viewport. Enhancement only, and built so
  that every failure mode ends with the content VISIBLE:

    - No script (blocked, failed to load, threw): nothing is ever hidden,
      because the hiding is applied BY this file. The markup ships visible.
    - No IntersectionObserver: returns before touching anything.
    - prefers-reduced-motion: reduce: returns before touching anything, and
      base.css carries a matching rule so a section already mid-animation when
      the setting changes snaps to visible rather than sticking at zero opacity.

  That is the whole reason the hidden state is added at runtime rather than
  shipped in the CSS. A stylesheet that hides content and waits for a script is
  one network failure away from a blank page.

  ── What it does NOT animate ────────────────────────────────────────────────
    1. The FIRST section of the page. That is the hero, and it holds the LCP
       element (§10) — an LCP image that fades in reports a later LCP, so the
       one section that must paint immediately is never touched.
    2. Anything already on screen when the script runs. Fading in something the
       visitor is already looking at is a flash, not an entrance.
    3. Any section containing [data-rns-no-reveal] — the sticky shop bar opts
       out this way, because it owns its own reveal, and because an ancestor at
       opacity below 1 becomes the containing block for a position:fixed
       descendant and would strand that bar in a zero-height wrapper.
    4. Any section with no rendered height. Several sections legitimately render
       nothing on the storefront until their images exist (proof-before-after),
       and a zero-height box has no entrance to make. This also catches a
       fixed-position section whose opt-out attribute went missing.
    5. Anything, in the theme editor. A merchant arranging a page should see
       their content, not watch it animate on every re-render.

  ── Why it is loaded by the layout ──────────────────────────────────────────
  §6 has each section load the script it needs, and this is the one exception:
  it belongs to no section, it applies to whichever sections a template happens
  to stack, and duplicating it into each would run one observer per section for
  a single page-wide behavior.

  Only opacity and transform are animated. Neither is a layout property, so this
  cannot contribute to CLS (§10).

  Diagnostics: append ?rns-debug to the URL to have it narrate what it found and
  why it stopped. Every early return says so, because the whole class of bug
  this file is prone to is failing quietly and looking identical to "off".
*/

(function () {
  'use strict';

  const DEBUG = window.location.search.indexOf('rns-debug') !== -1;
  const log = function () {
    if (!DEBUG) return;
    const args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[rns-reveal]'].concat(args));
  };

  if (!('IntersectionObserver' in window)) {
    log('stopped: no IntersectionObserver. Content stays visible.');
    return;
  }

  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    log(
      'stopped: prefers-reduced-motion is REDUCE, so nothing animates. This is',
      'correct behaviour, not a fault. On Windows it is Settings ▸ Accessibility',
      '▸ Visual effects ▸ Animation effects; on macOS, Accessibility ▸ Display ▸',
      'Reduce motion. DevTools can also force it: Rendering ▸ Emulate CSS',
      'prefers-reduced-motion.'
    );
    return;
  }

  if (window.Shopify && window.Shopify.designMode) {
    log('stopped: theme editor. Animation is skipped so the merchant sees content.');
    return;
  }

  const main = document.getElementById('MainContent') || document.querySelector('main');
  if (!main) {
    log('stopped: no <main> element found.');
    return;
  }

  // Widened deliberately: direct children first, then any descendant section,
  // then plain element children. A selector that matched nothing used to mean
  // this file did nothing and said nothing.
  let sections = main.querySelectorAll(':scope > .shopify-section');
  let how = 'direct .shopify-section children';
  if (!sections.length) {
    sections = main.querySelectorAll('.shopify-section');
    how = 'descendant .shopify-section';
  }
  if (!sections.length) {
    sections = main.children;
    how = 'element children of <main>';
  }

  log('found', sections.length, 'sections via', how);

  if (sections.length < 2) {
    log('stopped: fewer than two sections, so there is nothing below the hero.');
    return;
  }

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('rns-reveal--in');
        log('revealed', entry.target.id || entry.target.tagName);
        // One entrance per section. Re-animating on the way back up is the
        // thing that makes scroll animation feel like a toy.
        observer.unobserve(entry.target);
      });
    },
    // A tenth of the viewport of overlap before it counts, so a section starts
    // arriving rather than snapping in the instant its first pixel appears.
    { threshold: 0, rootMargin: '0px 0px -10% 0px' }
  );

  const viewport = window.innerHeight || document.documentElement.clientHeight;
  let observed = 0;
  const skipped = [];

  // Index 0 is skipped outright: it is the hero and it owns the LCP element.
  for (let i = 1; i < sections.length; i++) {
    const section = sections[i];
    const name = section.id || section.tagName + '#' + i;

    if (section.querySelector('[data-rns-no-reveal]')) {
      skipped.push(name + ' (opted out)');
      continue;
    }

    if (!section.offsetHeight) {
      skipped.push(name + ' (no rendered height)');
      continue;
    }

    if (section.getBoundingClientRect().top < viewport) {
      skipped.push(name + ' (already on screen)');
      continue;
    }

    section.classList.add('rns-reveal');
    observer.observe(section);
    observed++;
  }

  log('animating', observed, 'section(s); skipped:', skipped.length ? skipped.join(', ') : 'none');

  if (!observed) {
    log(
      'nothing is animating. If every section says "already on screen", the',
      'window is taller than the page, or this ran before the stylesheets laid',
      'the page out.'
    );
  }
})();
